import intl from "react-intl-universal";
import { UploadVolumeNegotiation, UploadVolumeNegotiationEnCSVEmpty, UploadVolumeNegotiationEsCSVEmpty, UploadVolumeNegotiationPtBrCSVEmpty, UploadVolumeNegotiationRequiredFieldsEn, UploadVolumeNegotiationRequiredFieldsEs, UploadVolumeNegotiationRequiredFieldsPtBr } from "./types/volume-negotiation.types";
import { getLanguageCode } from "locales";
import { formatAndValidateNumber } from "./utils";
import { importNegotiationVolumes } from "services/volume-orders.service";
import { getDateInISOFormat } from "utils/format-date";
import { formatErrorMessage } from "utils/handle-error";

interface IValidationsVolumeNegotiationResult {
  isValid: boolean,
  validationMessage: string[]
  data: UploadVolumeNegotiation[],
}

export const validateCsvEmpty = () => {
  switch (getLanguageCode()) {
    case "pt":
      return UploadVolumeNegotiationPtBrCSVEmpty;
    case "es":
      return UploadVolumeNegotiationEsCSVEmpty;
    case "en":
    default:
      return UploadVolumeNegotiationEnCSVEmpty;
  }
};

export const validateRequiredFields = () => {
  switch (getLanguageCode()) {
    case "pt":
      return UploadVolumeNegotiationRequiredFieldsPtBr;
    case "es":
      return UploadVolumeNegotiationRequiredFieldsEs;
    case "en":
    default:
      return UploadVolumeNegotiationRequiredFieldsEn;
  }
};

const getMissingFields = (data: string[]) => (`${data.join(', ')}.`);

export const formatAndValidateVolumeNegotiationFile: (items: UploadVolumeNegotiation[]) => IValidationsVolumeNegotiationResult = (items: UploadVolumeNegotiation[]) => {
  const _result: IValidationsVolumeNegotiationResult = {
    data: [],
    isValid: true,
    validationMessage: []
  }

  let _items = items[0] as unknown as UploadVolumeNegotiation[];
  _items = _items?.filter(x => !Object.values(x).every(value => !value || value.trim() === ''));

  if (!_items?.length) {
    _result.isValid = false;
    _result.validationMessage.push(intl.get('master-data.general-configuration.invalidFile'));
    return _result;
  }

  const UploadNegotiationVolumesCSVEmpty = validateCsvEmpty();
  const validateHeaders = Object.keys(UploadNegotiationVolumesCSVEmpty).every(x => Object.keys(_items[0]).includes(x));

  if (!validateHeaders) {
    _result.isValid = false;
    _result.validationMessage.push(intl.get('master-data.general-configuration.incorrectHeader'));
    return _result;
  }

  let line = 2;

  for (const x of _items) {
    const requiredFields = validateRequiredFields();
    const dataMissing = requiredFields?.filter((field) => {
      const value = x[field];
      return !value || value.trim() === ''
    });

    if (dataMissing.length !== 0) {
      _result.isValid = false;
      _result.validationMessage.push(`${intl.get('master-data.general-configuration.missingRequiredFields')} ${line}: ${getMissingFields(dataMissing)}`)
      line++;
      continue;
    }

    const lineValidations: string[] = [];
    try {
      const item: UploadVolumeNegotiation = x as unknown as UploadVolumeNegotiation;
      const quantityIntl = intl.get('volumeOrders.negotiationImport.quantity');

      const _quantity = formatAndValidateNumber(x[quantityIntl], lineValidations, quantityIntl);

      item[quantityIntl] = _quantity;

      if (lineValidations.length) {
        _result.isValid = false;
        _result.validationMessage.push(`${intl.get('commons.line')} ${line} - ${intl.get('master-data.general-configuration.fieldErrors')} ${lineValidations.join(', ')}`)
        line++;
        continue;
      }

      _result.data.push(item)
    }
    catch (e) {
      _result.isValid = false;
      _result.validationMessage.push(`${intl.get('master-data.general-configuration.errorLine')} ${line}`)
      console.error(e);
    }

    line++;
  }

  return _result;
}

export const createVolumeNegotiationPromises = async (
  fileData: UploadVolumeNegotiation[],
  treatSuccess: (data: any) => void,
  treatError: () => void,
  setProcessingPercentual: React.Dispatch<React.SetStateAction<number>>
) => {
  const responseList = [];
  const processingFileLength = fileData.length;
  let index = 1;

  const groupedByVolumeCodeFileData = Object.groupBy(fileData, ({ [intl.get('volumeOrders.negotiationImport.volumeOrderCode')]: volumeOrderCode }) => volumeOrderCode);

  for(const volumeOrderGroup of Object.values(groupedByVolumeCodeFileData)) {
    const payloadData = {
      volumeOrders: volumeOrderGroup?.map((x: UploadVolumeNegotiation, _index: number) => ({
        line: index + _index,
        volumeOrderCode: x[intl.get('volumeOrders.negotiationImport.volumeOrderCode')],
        supplierCode: x[intl.get('volumeOrders.negotiationImport.supplierCode')],
        supplierDescription: x[intl.get('volumeOrders.negotiationImport.supplierDescription')],
        center: x[intl.get('volumeOrders.negotiationImport.destinationCode')],
        centerDescription: x[intl.get('volumeOrders.negotiationImport.descriptionDestination')],
        materialNumber: x[intl.get('commons.materialCode')],
        materialDescription: x[intl.get('volumeOrders.negotiationImport.materialDescription')],
        pickupDate: getDateInISOFormat(x[intl.get('volumeOrders.negotiationImport.pickupDate')]),
        deliveryDate: getDateInISOFormat(x[intl.get('volumeOrders.negotiationImport.deliveryDate')]),
        quantity: Number(x[intl.get('volumeOrders.negotiationImport.quantity')]),
        typeJustificationManualSuggestion: Number(x[intl.get('commons.justification')]),
        comments: x[intl.get('commons.observation')],
        acceptTotal: x[intl.get('volumeOrders.negotiationImport.fullAcceptante')],
        refuse: x[intl.get('volumeOrders.negotiationImport.totalRefusal')]
      }))
    }

    try {
      const response = await importNegotiationVolumes(payloadData);

      if(response?.data?.AllSuccess) {
        const _linesPayload = payloadData?.volumeOrders?.length ?? 1;
        for(let i = 0; i < _linesPayload; i++) {
          responseList.push(response.data);
        }
      } else {
        responseList.push(response.data);
      }
    } catch (e) {
      responseList.push({
        AllSuccess: false,
        Results: [{
          Type: 0,
          Message: formatErrorMessage(e)
        }]
      });
    }
    setProcessingPercentual(((index/processingFileLength)*100));
    index += volumeOrderGroup?.length ?? 1;
  };

  if (responseList.length > 0) {
    treatSuccess(responseList);
  } else
    treatError();
};
